import React, { useCallback, useEffect, useState } from 'react'
import { Stack } from '@mui/material'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import SmartSelect from '../../generic/smartSelect/SmartSelect'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { searchDocumentRepositoryRecords, documentRepositoryTablePage } from './documentRepositoryApi'
import { topicLabelKey } from './documentRepositoryTopics'

// G2-3/V3b (03-dominio.md, 02-reglas.md) — R3 is the ONE endpoint with its OWN, smaller page-size
// rule: `size` 1..50 (400 above 50), default 25. D10 left R3 untouched (`contrato-sun.md` §7 D10:
// "Sin cambios: … R3-R8"). `rowsPerPageOptions` is capped to that range; the default page size (10)
// is simply what the developer chose for this compact widget, well inside the 1..50 window.
const RECORD_PICKER_ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50]
const RECORD_PICKER_DEFAULT_PAGE_SIZE = 10

const workflowVersionOf = (record) => {
  const workflowSegment = (record.path || []).find((segment) => segment.topic === 'WORKFLOW')
  return workflowSegment && workflowSegment.version ? workflowSegment.version : ''
}

/**
 * DocumentRepositoryRecordPicker — historia 170, HU-02 (J2). R3's own buscador (`03-dominio.md`
 * R3): choosing a topic restricts the search to it (RN27: the record filter "implica su temática",
 * DG-12); `topics` is expected to already be filtered to `recordSearch === true` entries by the
 * caller — this component never widens that set (RN21 mirror: a topic without `recordSearch` never
 * offers records to filter by, same reasoning as "no se ofrece como destino" for uploads).
 *
 * Refactor (5ª ronda) — the results list is now a small `SmartMateriaUITable` in `pageable` mode
 * instead of a plain text box + a `Stack` of clickable buttons: `dataFetch` calls R3
 * (`searchDocumentRepositoryRecords`) with the table's own `requestPagination.genericSearch`/`page`/
 * `size`, and `documentRepositoryTablePage` (already shared with R2's own adapter, `documentRepositoryApi.js`)
 * reshapes R3's `{content, totalElements, page, size}` into the `{content, totalElements, number, size}`
 * the table expects — the two response shapes coincide field-for-field, so no new adapter was needed.
 * Only `code`/`name` are shown (same two fields the old buttons rendered, `record.code — record.name`)
 * — `path` (when R3's response carries it) is deliberately left out: this is a small picker embedded
 * in a dialog, not the main listing, and a breadcrumb column would not fit; declared here rather than
 * left implicit. No column filter or server-side sort (`sortable: false`/`search: false` on both
 * columns): R3 offers neither (`03-dominio.md` R3 — only `text`/`page`/`size`), only the table's own
 * general search bar reaches it (`genericSearch`).
 *
 * `key={selectedTopic}` remounts the table whenever the topic changes — the simplest way to satisfy
 * "always starts at page 0 with an empty search box for the newly chosen topic" (a fresh instance
 * already starts there; no separate `resetPageable`/`initialPage` bookkeeping needed).
 *
 * `onSelect(topic, referenceId, record)` — the 3rd argument (the full R3 row, `{referenceId, code,
 * name, path}`) is additive: `DocumentRepositoryUploadDialog.jsx` (this component's other caller)
 * only reads the first two and ignores it; `DocumentRepositoryAssociatedRecordFilter.jsx` (the
 * screen's own record filter, refactor 3rd round) uses it to show a human label without a second
 * lookup (there is no "get one record by id" endpoint, only R3's own search). Since the 5th round,
 * `record` also carries `SmartMateriaUITable`'s own internal bookkeeping field
 * (`'smart-table-index'`, added to every row before rendering) — harmless, neither caller reads
 * anything beyond `code`/`name`.
 */
const DocumentRepositoryRecordPicker = ({ topics, onSelect }) => {
  const { t_ } = useSmartTranslation()
  const [selectedTopic, setSelectedTopic] = useState(topics[0] ? topics[0].topic : '')

  // `topics` (RN27/G1-8 DG-12: only recordSearch topics) can arrive AFTER first mount — R1 has not
  // resolved yet when the picker is first rendered (e.g. inside the toolbar's own filter, or the
  // upload dialog mounted before R1's own effect settles). The lazy useState initializer above only
  // runs once, so once R1 resolves this keeps the selection in sync with whatever topics are offered.
  useEffect(() => {
    if (!topics.some((topic) => topic.topic === selectedTopic)) {
      setSelectedTopic(topics[0] ? topics[0].topic : '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topics])

  // Stable only for as long as `selectedTopic` itself does not change — and when it DOES change, the
  // table remounts anyway (`key={selectedTopic}` below), so a fresh `dataFetch` closure for the new
  // topic is exactly what that fresh instance should call from its very first fetch.
  const dataFetch = useCallback((requestPagination) => {
    return searchDocumentRepositoryRecords(selectedTopic, requestPagination.genericSearch, requestPagination.page, requestPagination.size)
      .then(documentRepositoryTablePage)
      .catch(() => ({ content: [], totalElements: 0, number: requestPagination.page, size: requestPagination.size }))
  }, [selectedTopic])

  // WORKFLOW records also show their version (developer request): read from the record's own WORKFLOW
  // path segment, the same `version` R2 already carries in its rows (contrato-sun.md §3). Empty while
  // R3 still returns `path = []` (encargo-sun-r3-workflow-version.md).
  const columns = [
    { name: t_('Code'), field: 'code', sortable: false, search: false },
    { name: t_('Name'), field: 'name', sortable: false, search: false },
    ...(selectedTopic === 'WORKFLOW'
      ? [{ name: t_('Version'), field: 'version', sortable: false, search: false, render: workflowVersionOf }]
      : [])
  ]

  return (
    <Stack spacing={1} sx={{ p: 1, minWidth: '28rem' }}>
      <Stack direction='row' justifyContent='flex-end'>
        <SmartSelect
          label={t_('Select the topic')}
          width='12rem'
          disableClearable
          autoSelectFirst={false}
          selectableOptions={topics.map((topic) => topic.topic)}
          value={selectedTopic || null}
          optionLabelTransform={(topic) => t_(topicLabelKey(topic))}
          onChange={(topic) => setSelectedTopic(topic || '')}
        />
      </Stack>
      {selectedTopic && (
        <SmartMateriaUITable
          key={selectedTopic}
          tableTuneUniqueKey=''
          columns={columns}
          pageable
          dense
          dataFetch={dataFetch}
          tablePageSize={RECORD_PICKER_DEFAULT_PAGE_SIZE}
          rowsPerPageOptions={RECORD_PICKER_ROWS_PER_PAGE_OPTIONS}
          searchPosition='left'
          onRowClick={(row) => onSelect(selectedTopic, row.referenceId, row)}
        />
      )}
    </Stack>
  )
}

export default DocumentRepositoryRecordPicker
